import { useState } from "react";
import { toast } from "sonner";
import { ArrowDown, ArrowUp, Check, Layers, Loader2 } from "lucide-react";
import api, { getThumbnailUrl } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const MERGEABLE = (v) => !["generating", "queued", "failed"].includes(v.status);

const EpisodeRow = ({ ep, index, total, checked, onToggle, onMove }) => (
  <div data-testid={`series-episode-${ep.id}`}
    className={`flex items-center gap-3 rounded-lg border p-2 transition-colors ${checked ? "border-[#00F0FF]/60 bg-[#00F0FF]/5" : "border-border/50"}`}>
    <button type="button" onClick={onToggle} disabled={!MERGEABLE(ep)} data-testid={`series-episode-check-${ep.id}`}
      className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${checked ? "border-[#00F0FF] bg-[#00F0FF]" : "border-white/40"} disabled:opacity-30`}>
      {checked && <Check className="h-3.5 w-3.5 text-black" />}
    </button>
    <div className="h-16 w-10 shrink-0 overflow-hidden rounded bg-black">
      {getThumbnailUrl(ep) && <img src={getThumbnailUrl(ep)} alt="" className="h-full w-full object-cover" />}
    </div>
    <div className="min-w-0 flex-1">
      <Badge data-testid={`series-episode-badge-${ep.id}`} className="mb-1 bg-[#A855F7]/20 text-[#C084FC] border-0">Ep {ep.episode_number || index + 1}</Badge>
      <p className="truncate text-sm text-white">{ep.title}</p>
      {!MERGEABLE(ep) && <p className="text-xs text-muted-foreground">Not ready to merge ({ep.status})</p>}
    </div>
    <div className="flex flex-col">
      <button type="button" disabled={index === 0} onClick={() => onMove(-1)} data-testid={`series-episode-up-${ep.id}`}
        className="p-1 text-muted-foreground hover:text-white disabled:opacity-20"><ArrowUp className="h-4 w-4" /></button>
      <button type="button" disabled={index === total - 1} onClick={() => onMove(1)} data-testid={`series-episode-down-${ep.id}`}
        className="p-1 text-muted-foreground hover:text-white disabled:opacity-20"><ArrowDown className="h-4 w-4" /></button>
    </div>
  </div>
);

export const SeriesDialog = ({ series, onClose, onMerged }) => {
  const [order, setOrder] = useState(() => series.episodes);
  const [picked, setPicked] = useState(() => new Set(series.episodes.filter(MERGEABLE).map((e) => e.id)));
  const [transition, setTransition] = useState("crossfade");
  const [merging, setMerging] = useState(false);

  const move = (i, dir) => setOrder((o) => {
    const next = [...o];
    [next[i], next[i + dir]] = [next[i + dir], next[i]];
    return next;
  });
  const toggle = (id) => setPicked((p) => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const ids = order.filter((e) => picked.has(e.id)).map((e) => e.id);

  const merge = async () => {
    setMerging(true);
    try {
      await api.post("/series/merge", { video_ids: ids, transition });
      toast.success("Merging your episodes - the full video will appear in your Library shortly.");
      onMerged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't merge these episodes");
      setMerging(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="series-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Layers className="h-5 w-5 text-[#A855F7]" />{series.title}</DialogTitle>
          <DialogDescription>{series.episodes.length} episodes. Tick the ones to join, reorder them, then merge into one video. Free.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] space-y-2 overflow-y-auto pr-1">
          {order.map((ep, i) => (
            <EpisodeRow key={ep.id} ep={ep} index={i} total={order.length} checked={picked.has(ep.id)}
              onToggle={() => toggle(ep.id)} onMove={(d) => move(i, d)} />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <Select value={transition} onValueChange={setTransition}>
            <SelectTrigger className="flex-1" data-testid="series-transition-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="crossfade" data-testid="series-transition-crossfade">Smooth crossfade</SelectItem>
              <SelectItem value="fade_black" data-testid="series-transition-fade-black">Fade through black</SelectItem>
              <SelectItem value="cut" data-testid="series-transition-cut">Straight cut</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={merge} disabled={ids.length < 2 || merging} data-testid="series-merge-btn"
            className="bg-gradient-to-r from-[#00F0FF] to-[#A855F7] text-black font-semibold">
            {merging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Layers className="mr-2 h-4 w-4" />}
            Merge {ids.length} episodes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
